import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Box, Flex, styled } from '@withjoy/joykit';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { useIsCompactBrannanHeader } from '../hooks/useIsCompactBrannanHeader';
import { shouldHideStandaloneVideo } from './shouldHideStandaloneVideo';
import { shouldOverlayStandaloneNav } from './standaloneNavOverlay';
import { VideoBackgroundRenderer } from './VideoBackground';
import { MonogramByContainerId, MONOGRAM_SIZE } from './MonogramContent';
import { MonogramEventDisplayName } from './MonogramEventDisplayName';
import { useHandleEditMonogramClick } from './useHandleEditMonogramClick';
import type { VideoBackgroundMonogramLayoutProps } from './VideoBackgroundMonogram.types';

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const VideoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.videoBackgroundMonogram.aspectRatio as number;

export const VideoBackgroundMonogramLayout: FC<VideoBackgroundMonogramLayoutProps> = props => {
  const {
    websiteHeader,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const { isCompactView } = useIsCompactBrannanHeader({
    pages,
    pageSlug,
    currentPageType,
    brannanNavigation: restProps.eventDesign?.brannanNavigation,
  });

  const isMobileScreen = useIsMobileScreen();
  const skipAnimation = useSkipInitialAnimation();

  const hideVideoArgs = {
    isCompactView,
    navBarVariant: restProps.navBarVariant,
    showVideoOnlyOnWelcomePage: websiteHeader.headerPhoto.showVideoOnlyOnWelcomePage,
  };
  const shouldHideVideoBackground = shouldHideStandaloneVideo(hideVideoArgs);
  const isOverlayNav = shouldOverlayStandaloneNav(hideVideoArgs);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : isMobileScreen ? '90svh' : '100svh',
            aspectRatio: `${CONTAINER_ASPECT_RATIO}`,
          },
    [isCompactView, isInlineEditing, isMobileScreen, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobileScreen ? '90svh' : '100svh';

  const { video, navigationStyle, jsonContainerId, monogramColor, monogramEventName } = websiteHeader.headerPhoto;
  const videoUrl = isMobileScreen && video?.mobileUrl ? video.mobileUrl : video?.desktopUrl;
  const showEventDisplayName = !!monogramEventName?.showEventDisplayName && !!monogramEventName.content;

  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHideVideoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHideVideoBackground ? 'auto' : height,
      containerMinHeight: shouldHideVideoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHideVideoBackground ? 'visible' : 'hidden',
    };
  }, [shouldHideVideoBackground, aspectRatio, height]);

  const handleEditVideoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderVideo' },
    });
  });

  const handleEditMonogramClick = useHandleEditMonogramClick();

  const { tq } = useTranslation('inlineEditor');

  const eventNameNavProps = {
    eventDisplayName: websiteHeader.eventDisplayName,
    colors: websiteHeader.headerPhoto.colors,
    navigationStyle,
    // Monogram is the focal element; the event display name (hero title) is intentionally hidden,
    // so there is no inline title-edit action to wire for this layout.
    hideEventName: true,
    isCompactView,
    currentPageType,
    isContentEditable: isInlineEditing,
    editableContentRef,
    forceRenderViewIn,
    scaleValue,
    pages,
    pageSlug,
    renderedContainerHeight,
    renderedContainerWidth,
    viewportHeightFallback,
    ...restProps,
  };

  // Centered monogram overlaid on the video background, with the optional event display name
  // stacked directly below it. Title/nav still render via EventNameNavForStandaloneHeader.
  // Hidden in compact view (classic nav on non-welcome pages), where the header collapses.
  const monogramOverlay =
    !isCompactView && (jsonContainerId || showEventDisplayName) ? (
      <Flex
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        zIndex="banner"
        pointerEvents="none"
      >
        {jsonContainerId && (
          <InlineEditor
            elementLabel={tq`Monogram`}
            wrapperType="actionInside"
            actionData={{ editMonogram: handleEditMonogramClick }}
            componentName="headerPhoto"
            pageName={currentPageType}
            pageSlug={pageSlug || ''}
            // Caps wrapper at 432px (MonogramView's maxWidth), preventing hover from firing across
            // the full-width centering overlay. min() keeps it responsive on narrow screens.
            // pointerEvents:auto re-enables hover here; the surrounding overlay stays pointerEvents="none".
            wrapperCSS={{ width: `min(${MONOGRAM_SIZE}px, 100%)`, pointerEvents: 'auto' }}
          >
            <MonogramByContainerId
              jsonContainerId={jsonContainerId}
              showPlaceholder={showPlaceholder}
              fontColor={monogramColor}
            />
          </InlineEditor>
        )}
        {showEventDisplayName && (
          <MonogramEventDisplayName
            content={monogramEventName?.content ?? ''}
            color={monogramEventName?.color ?? monogramColor}
          />
        )}
      </Flex>
    ) : null;

  return (
    <VideoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHideVideoBackground ? (
          <EventNameNavForStandaloneHeader
            {...eventNameNavProps}
            isBackgroundHidden
            isOverlayNav={isOverlayNav}
          />
        ) : (
          <VideoBackgroundRenderer
            showPlaceholder={showPlaceholder}
            skipAnimation={skipAnimation}
            videoUrl={videoUrl}
            thumbnailUrl={video?.placeholderImageUrl}
            height={height}
            isCompactView={isCompactView}
            isMobile={isMobileScreen && !!video?.mobileUrl}
            currentPageType={currentPageType}
            pageSlug={pageSlug}
            handleEditVideoClick={handleEditVideoClick}
            editActionVerticalAlign="top"
          >
            {monogramOverlay}
            <EventNameNavForStandaloneHeader {...eventNameNavProps} />
          </VideoBackgroundRenderer>
        )}
      </Flex>
      {isMobileScreen && !isCompactView && !shouldHideVideoBackground && !isInlineEditing && <BouncingChevron />}
    </VideoBackgroundOuter>
  );
};

VideoBackgroundMonogramLayout.displayName = 'VideoBackgroundMonogramLayout';
